import {Message} from "primereact/message";
import {type ReactNode, useMemo, useState} from "react";

import type {SnapshotMetadata} from "@reva/shared/valuation-data";

import ReopenForCorrectionDialog from "../../component/ReopenForCorrectionDialog";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useCanStartCorrection, useLiveDataEntry, useLoadValuationState} from "../../hooks/valuationLoadHooks";
import {
    selectIsSnapshot,
    selectLoadedSnapshotNumber,
    selectSnapshotMetadata,
    selectSnapshots,
} from "../../redux/persistenceSlice";
import {useRevaSelector} from "../../redux/revaHooks";
import {formatDateAndTime} from "../../util/dateUtil";

/**
 * Notice banner shown while viewing a loaded (frozen) snapshot.
 *
 * Line 1 states the snapshot type, the creating user (full name if available) and the
 * date/time. Up to two optional navigation lines let the user move between data states:
 *  - "back": jump to the predecessor (next older) snapshot, if any;
 *  - "forward": jump to the successor (next newer) snapshot, or — while on the newest
 *    snapshot — continue the live data entry / correction, or start a correction
 *    (depending on the live lifecycle state and the user's permissions).
 * Only rendered while a snapshot is currently loaded; otherwise null.
 */
export default function SnapshotMessage() {
    const {t, language} = useRevaTranslation();
    const isSnapshot = useRevaSelector(selectIsSnapshot);
    const metadata = useRevaSelector(selectSnapshotMetadata);
    const snapshots = useRevaSelector(selectSnapshots);
    const loadedSnapshotNumber = useRevaSelector(selectLoadedSnapshotNumber);
    const {loadSnapshot, loadLiveData} = useLoadValuationState();
    const liveDataEntry = useLiveDataEntry();
    const canStartCorrection = useCanStartCorrection();
    const [correctionDialogVisible, setCorrectionDialogVisible] = useState(false);

    // Predecessor (next older) and successor (next newer) snapshot of the loaded one.
    // selectSnapshots is already sorted ascending by number; sort defensively all the same.
    const {predecessor, successor} = useMemo(() => {
        const sorted = [...(snapshots ?? [])].sort((a, b) => a.number - b.number);
        const idx = sorted.findIndex((s) => s.number === loadedSnapshotNumber);
        return {
            predecessor: idx > 0 ? sorted[idx - 1] : undefined,
            successor: idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : undefined,
        };
    }, [snapshots, loadedSnapshotNumber]);

    if (!isSnapshot || !metadata) {
        return null;
    }

    const user = metadata.userFullName ?? metadata.user;
    const dateTime = formatDateAndTime(metadata.date, language);
    // Nur Final/PreFinal sind als geladener Snapshot-Typ relevant; sonst Rückfall auf den rohen Typ.
    const type = t(`snapshotType.${metadata.type}`, {defaultValue: metadata.type});

    // Interpolation values describing another snapshot ("Finalisierung von … vom …").
    const describe = (snapshot: SnapshotMetadata) => ({
        type: t(`snapshotType.${snapshot.type}`, {defaultValue: snapshot.type}),
        user: snapshot.userFullName ?? snapshot.user,
        date: formatDateAndTime(snapshot.date, language),
    });

    // A navigation line: an explanatory sentence followed by an inline action link.
    const navLine = (text: string, linkLabel: string, onClick: () => void) => (
        <span>
            {text}{" "}
            <span className="cursor-pointer text-primary" style={{textDecoration: "underline"}} onClick={onClick}>
                {linkLabel}
            </span>
        </span>
    );

    // Forward line: jump to the successor snapshot, or — while on the newest snapshot — start a
    // correction (live Final), or continue the live data entry / correction. Exactly one variant.
    // Both decisions (start correction / load the live state + its label) are shared with the
    // lifecycle menu via useCanStartCorrection() / useLiveDataEntry(); loading the live Final
    // state is intentionally not offered, a correction is started instead.
    let forwardLine: ReactNode = null;
    if (successor) {
        forwardLine = navLine(
            t("object.snapshotNavNewer", describe(successor)),
            t("object.snapshotNavNewerLink"),
            () => void loadSnapshot(successor.number),
        );
    } else if (canStartCorrection) {
        forwardLine = navLine(t("object.snapshotNavFinalHint"), t("value.actions.correction"), () =>
            setCorrectionDialogVisible(true),
        );
    } else if (liveDataEntry) {
        forwardLine = navLine(t("object.snapshotNavCurrentHint"), t(liveDataEntry.labelKey), () => void loadLiveData());
    }

    return (
        <>
            <Message
                className="w-full mb-4"
                content={
                    <div className="flex flex-col gap-1">
                        <span>{t("object.snapshotDescription", {type, user, dateTime})}</span>
                        {predecessor &&
                            navLine(
                                t("object.snapshotNavOlder", describe(predecessor)),
                                t("object.snapshotNavOlderLink"),
                                () => void loadSnapshot(predecessor.number),
                            )}
                        {forwardLine}
                    </div>
                }
            />
            <ReopenForCorrectionDialog
                visible={correctionDialogVisible}
                onHide={() => setCorrectionDialogVisible(false)}
            />
        </>
    );
}
